import React, { useState } from 'react';
import { Crown, Sparkles, Check, X } from 'lucide-react';
import { db, auth } from '../firebase-config';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';

/**
 * AI Payment Modal - PRO/PREMIUM Tier Upgrade
 */
export default function AIPaymentModal({ open, onClose, currentTier = 'free' }) {
  const [selectedTier, setSelectedTier] = useState('pro');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const tiers = {
    pro: {
      name: 'PRO',
      price: 2990,
      priceMonthly: 2990,
      features: [
        '5 kép átalakítás',
        'Összes stílus elérhető',
        'AI ár optimalizálás',
        'Eladási javaslatok',
        'Prioritás támogatás'
      ],
      color: 'from-blue-600 to-purple-600',
      icon: Sparkles
    },
    premium: {
      name: 'PREMIUM',
      price: 9990,
      priceMonthly: 9990,
      features: [
        '10+ kép (teljes lakás)',
        'Összes stílus elérhető',
        'Marketing csomag',
        'Hirdetés kiemelés',
        'AI hirdetésszöveg',
        'Profi tanácsadás',
        'Dedikált support'
      ],
      color: 'from-yellow-600 to-orange-600',
      icon: Crown,
      badge: 'LEGJOBB AJÁNLAT'
    }
  };

  const handleUpgrade = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('Jelentkezz be a vásárláshoz!');
      }

      // TODO: Integrate real payment gateway (Stripe/Barion)
      // For MVP: Simulate payment
      const paymentId = `payment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Update user tier in Firestore
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        aiTier: selectedTier,
        aiImagesUsed: 0, // Reset usage
        aiTierUpgradedAt: serverTimestamp(),
        aiPaymentId: paymentId
      });

      // Show success message
      alert(`Sikeres vásárlás! ${tiers[selectedTier].name} tier aktiválva.`);
      
      onClose();
      
      // Reload page to update UI
      window.location.reload();

    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Hiba történt a fizetés során');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStripePayment = async () => {
    // TODO: Implement Stripe payment
    // const stripe = await loadStripe(STRIPE_PUBLIC_KEY);
    // const session = await createCheckoutSession(selectedTier);
    // stripe.redirectToCheckout({ sessionId: session.id });
    
    handleUpgrade(); // For now, use mock payment
  };

  const handleBarionPayment = async () => {
    // TODO: Implement Barion payment
    // const barionResponse = await createBarionPayment(selectedTier);
    // window.location.href = barionResponse.paymentUrl;
    
    handleUpgrade(); // For now, use mock payment
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-5xl bg-[#0a0a0a] rounded-2xl border border-white/[0.08] shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        {/* Header */}
        <div className="text-center p-6 sm:p-8 border-b border-white/10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2">
            Válaszd ki a csomagod
          </h2>
          <p className="text-gray-300 text-sm sm:text-base">
            Használd ki az AI teljes erejét az ingatlanod eladásához
          </p>
        </div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 sm:p-8">
          {Object.entries(tiers).map(([key, tier]) => {
            const Icon = tier.icon;
            const isSelected = selectedTier === key;
            const isCurrent = currentTier === key;

            return (
              <div
                key={key}
                onClick={() => setSelectedTier(key)}
                className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all ${
                  isSelected
                    ? 'border-secondary bg-secondary/10 scale-105'
                    : 'border-white/[0.08] bg-white/5 hover:border-white/40'
                }`}
              >
                {/* Badge */}
                {tier.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-block bg-gradient-to-r from-yellow-500 to-orange-500 text-black text-xs font-bold px-3 py-1 rounded-full">
                      {tier.badge}
                    </span>
                  </div>
                )}

                {/* Current tier badge */}
                {isCurrent && (
                  <div className="absolute top-4 right-4">
                    <span className="inline-block bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                      Jelenlegi
                    </span>
                  </div>
                )}

                {/* Icon */}
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${tier.color} flex items-center justify-center mb-4`}>
                  <Icon className="w-8 h-8 text-white" />
                </div>

                {/* Tier name */}
                <h3 className="text-2xl font-bold text-white mb-2">{tier.name}</h3>

                {/* Price */}
                <div className="mb-6">
                  <span className="text-4xl font-black text-white">
                    {tier.price.toLocaleString('hu-HU')}
                  </span>
                  <span className="text-gray-400 ml-2">Ft</span>
                  <p className="text-sm text-gray-400 mt-1">Egyszeri díj</p>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Select button */}
                {!isCurrent && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTier(key);
                    }}
                    className={`w-full py-3 rounded-lg font-bold transition-all ${
                      isSelected
                        ? 'bg-secondary text-white'
                        : 'bg-white/10 text-gray-300 hover:bg-white/20'
                    }`}
                  >
                    {isSelected ? 'Kiválasztva' : 'Kiválaszt'}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Error message */}
        {error && (
          <div className="mx-6 sm:mx-8 mb-6 p-4 bg-red-500/20 border border-red-500/40 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Payment buttons */}
        {currentTier !== selectedTier && (
          <div className="p-6 sm:p-8 border-t border-white/10">
            <div className="max-w-md mx-auto space-y-3">
              {/* Stripe payment */}
              <button
                onClick={handleStripePayment}
                disabled={isProcessing}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-4 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Feldolgozás...
                  </>
                ) : (
                  <>
                    💳 Fizetés bankkártyával (Stripe)
                  </>
                )}
              </button>

              {/* Barion payment */}
              <button
                onClick={handleBarionPayment}
                disabled={isProcessing}
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-bold py-4 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                🏦 Fizetés Barion-nal
              </button>

              <p className="text-xs text-gray-400 text-center mt-4">
                Biztonságos fizetés SSL titkosítással. Az adataidat nem tároljuk.
              </p>
            </div>
          </div>
        )}

        {/* Money back guarantee */}
        <div className="p-6 sm:p-8 bg-green-500/10 border-t border-green-500/20">
          <div className="flex items-center justify-center gap-3 text-green-400">
            <Check className="w-6 h-6" />
            <p className="font-medium">30 napos pénzvisszafizetési garancia</p>
          </div>
        </div>
      </div>
    </div>
  );
}
